import React, { CSSProperties, useEffect, useState } from 'react';
import {
    Text,
    IContextualMenuItem,
    Stack,
    PrimaryButton,
    ContextualMenu,
    IContextualMenuProps,
    IconButton,
    IButtonStyles,
    IIconProps,
    IStackStyles,
    IStackTokens,
} from '@fluentui/react';
import { logger, appStyles } from '../../../helpers';
import { useAppDispatch, useAppSelector } from '../../../store';
import { selectTranslate } from '../../../store/selectors/ui';
import { useWizard } from '../../../wizard';
import {
    FileOverwriteDialog,
    ProgressDialog,
    ServerFilesDialog,
} from '../../dialogs';
import WizardFiles from '../files';
import { PageHeading } from '../../common/pageHeading';
import { WizardFooter } from '../footer';
import { getRepeatedFileName } from '../../../helpers/file';
import {
    selectAppConfig,
    selectDestinationFolderPath,
    selectStubUrl,
} from '../../../store/selectors/app';
import {
    uploadShareLargeFileThunk,
    uploadShareFileThunk,
    UploadShareFileThunkError,
    chunkedUploadAbortControllerRefBox,
} from '../../../store/actions/app/thunks';
import {
    selectUser,
    selectUserId,
    selectTeamsPathUploadFiles,
} from '../../../store/selectors/auth';
import { countFilenameRepeats } from '../../../services/file';
import { DestinationFolder } from './dndFiles';
import { useFileProcessor } from '../../../helpers/hooks';
import { ServerFile } from '../../../model';
import { ErrOption } from '../../../helpers/model/errOption';
import { showNotification } from '../../../store/actions/app/actionCreators';
import {
    AppErrorCode,
    NotificationStatus,
} from '../../../store/actions/app/actionTypes';
import { WebdavApiErrorSubCode } from 'nextcloud-api/dist/models/error-model';
import { appActions } from '../../../store/actions/app/actions';
import { MEGABYTE } from '../../../helpers/constants';

const containerTokens: IStackTokens = { childrenGap: 10 };

const textStyle: CSSProperties = {
    fontSize: 14,
    fontStyle: 'normal',
    marginTop: 0,
};

const uploadContainerStyles: IStackStyles = { root: { alignItems: 'center' } };
const uploadButtonContainerTokens: IStackTokens = { childrenGap: 8 };

const folderIconButtonStyles: IButtonStyles = {
    root: { color: '#F36928 !important' },
};
const folderIconProps: IIconProps = {
    iconName: 'FabricFolder',
};

const wizardFilesContainerTokens: IStackTokens = { childrenGap: 15 };

export const SelectFileStep = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const translate = useAppSelector(selectTranslate);
    const destinationFolderPath = useAppSelector(selectDestinationFolderPath);
    const user = useAppSelector(selectUser);
    const stubUrl = useAppSelector(selectStubUrl);
    const userId = useAppSelector(selectUserId);
    const defaultUploadFolder = useAppSelector(selectTeamsPathUploadFiles);
    const appConfig = useAppSelector(selectAppConfig);

    const { setWizardStep } = useWizard();

    const [filesDialogHidden, setFilesDialogHidden] = useState(true);
    const [foldersDialogHidden, setFoldersDialogHidden] = useState(true);
    const [progressDialogHidden, setProgressDialogHidden] = useState(true);

    const {
        openFileSelector,
        plainFiles,
        clear: clearFiles,
    } = useFileProcessor();

    const [newFilename, setNewFileName] = useState('');
    const [fileOverwriteDialogHidden, setFileOverwriteDialogHidden] =
        useState(true);

    const uploadPath: DestinationFolder = destinationFolderPath
        ? destinationFolderPath
        : defaultUploadFolder;

    const file = plainFiles[0];

    useEffect(() => {
        if (!file) {
            return;
        }

        const uploadFile = async () => {
            let res:
                | ServerFile
                | ErrOption<UploadShareFileThunkError | WebdavApiErrorSubCode>;

            if (file.size <= appConfig.minChunkedUploadFileSizeMb * MEGABYTE) {
                res = await dispatch(
                    uploadShareFileThunk({
                        folder: uploadPath,
                        content: await file.arrayBuffer(),
                        fileBase: file.name,
                        allowOverwrite: false,
                    }),
                );
            } else {
                setProgressDialogHidden(false);
                res = await dispatch(
                    uploadShareLargeFileThunk({
                        folder: uploadPath,
                        file: file,
                        fileBase: file.name,
                        allowOverwrite: false,
                    }),
                );
                setProgressDialogHidden(true);
            }

            if (res instanceof Error) {
                logger.error(`uploadFile error: ${res.message}`, 'Upload');

                switch (res.cause) {
                    case UploadShareFileThunkError.FileExists:
                        const filenameRepeatCount = await countFilenameRepeats({
                            filename: file.name,
                            folder: uploadPath,
                            initialRepeatCount: 1,
                            requestConfig: {
                                stubUrl,
                                user,
                                userId,
                            },
                        });
                        setNewFileName(
                            getRepeatedFileName(file.name, filenameRepeatCount),
                        );
                        setFileOverwriteDialogHidden(false);
                        return;
                    case WebdavApiErrorSubCode.NetworkError:
                        dispatch(appActions.clearFailedChunkedFileUploadData());
                        dispatch(
                            showNotification({
                                errorCode: AppErrorCode.CustomError,
                                message: res.message,
                                status: NotificationStatus.Error,
                            }),
                        );
                        return;
                    default:
                        return clearFiles();
                }
            }

            clearFiles();
        };

        uploadFile();
    }, [file]);

    const onSelectLocalFile = (
        _ev?: React.MouseEvent<HTMLButtonElement>,
        _item?: IContextualMenuItem,
    ): void => {
        logger.info('onSelectLocalFile called', 'Files');
        openFileSelector();
    };

    const onSelectServerFile = (
        _ev?: React.MouseEvent<HTMLButtonElement>,
        _item?: IContextualMenuItem,
    ): void => {
        setFilesDialogHidden(false);
        logger.info('onSelectServerFile called', 'Files');
    };

    const onDestinationClick = (): void => {
        setFoldersDialogHidden(false);
    };

    const onOverwrite = async (overwrite: boolean): Promise<void> => {
        if (!file) {
            return;
        }

        const filename = overwrite ? file.name : newFilename;

        let res:
            | ServerFile
            | ErrOption<UploadShareFileThunkError | WebdavApiErrorSubCode>;

        if (file.size <= appConfig.minChunkedUploadFileSizeMb * MEGABYTE) {
            res = await dispatch(
                uploadShareFileThunk({
                    folder: uploadPath,
                    content: await file.arrayBuffer(),
                    fileBase: filename,
                    allowOverwrite: true,
                }),
            );
        } else {
            setProgressDialogHidden(false);
            res = await dispatch(
                uploadShareLargeFileThunk({
                    folder: uploadPath,
                    file: file,
                    fileBase: filename,
                    allowOverwrite: true,
                }),
            );
            setProgressDialogHidden(true);
        }

        if (res instanceof Error) {
            logger.error(`onOverwrite upload error: ${res.message}`, 'Upload');
            dispatch(appActions.clearFailedChunkedFileUploadData());

            switch (res.cause) {
                case WebdavApiErrorSubCode.NetworkError:
                    dispatch(
                        showNotification({
                            errorCode: AppErrorCode.CustomError,
                            message: res.message,
                            status: NotificationStatus.Error,
                        }),
                    );
                    return;
                default:
                    return;
            }
        }
    };

    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'localFiles',
                text: translate('main_page_local_files'),
                onClick: onSelectLocalFile,
            },
            {
                key: 'serverFiles',
                text: translate('main_page_server_files'),
                onClick: onSelectServerFile,
            },
        ],
        isBeakVisible: false,
        useTargetWidth: true,
    };

    const getMenu = (props: IContextualMenuProps): JSX.Element => {
        // Customize contextual menu with menuAs
        return <ContextualMenu {...props} />;
    };

    const cancelUploadAndHideDialog = (isHidden) => {
        const controller = chunkedUploadAbortControllerRefBox.getValue();
        controller.abort('User cancelled upload');
        setProgressDialogHidden(isHidden);
        dispatch(appActions.clearFailedChunkedFileUploadData());
    };

    return (
        <>
            <Stack tokens={containerTokens}>
                <PageHeading text="Share files" />
                <Text style={textStyle}>{translate('sharefiles_desc')}</Text>
                <Stack tokens={{ childrenGap: 10 }}>
                    <PrimaryButton
                        styles={appStyles.primaryButton}
                        style={{ width: '100%' }}
                        menuProps={menuProps}
                        menuAs={getMenu}
                        text={translate('sharefiles_localfiles_selectfile')}
                    />
                    <Stack
                        horizontal={true}
                        styles={uploadContainerStyles}
                        tokens={uploadButtonContainerTokens}
                    >
                        <IconButton
                            styles={folderIconButtonStyles}
                            iconProps={folderIconProps}
                            onClick={onDestinationClick}
                        />
                        <Text>{uploadPath}</Text>
                    </Stack>
                </Stack>
                <Stack tokens={wizardFilesContainerTokens}>
                    <WizardFiles
                        wizardPos={-1}
                        onEdit={setWizardStep}
                        onDestination={onDestinationClick}
                        hideDestinationFolder={true}
                    />
                </Stack>
            </Stack>
            <WizardFooter />
            <ServerFilesDialog
                hidden={filesDialogHidden}
                onDismiss={setFilesDialogHidden}
                renderFiles={true}
            />
            <ServerFilesDialog
                hidden={foldersDialogHidden}
                onDismiss={setFoldersDialogHidden}
                renderFiles={false}
            />
            <FileOverwriteDialog
                hidden={fileOverwriteDialogHidden}
                onDismiss={setFileOverwriteDialogHidden}
                fileName={file?.name ?? ''}
                fileNewName={newFilename}
                onOverwrite={onOverwrite}
            />
            <ProgressDialog
                hidden={progressDialogHidden}
                onDismiss={cancelUploadAndHideDialog}
                title={translate('progress_header')}
                subText={translate('progress_count')}
            />
        </>
    );
};
